import { axiosInstance } from '../utils/axiosinstance';

// Types for participant data
export interface Participant {
  _id: string;
  hackathonId?: {
    _id: string;
    title: string;
    startDate: string;
    endDate: string;
    status: string;
  };
  userId?: {
    _id: string;
    fullName: string;
    email: string;
    phoneNumber: string;
    state: string;
    gender: string;
    currentRole?: string;
    organization?: string;
  };
  teamId?: {
    _id: string;
    name: string;
    status: string;
  } | null;
  leader: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface ParticipantsResponse {
  success: boolean;
  message?: string;
  data: Participant[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    nextPage: number | null;
    prevPage: number | null;
  };
}

export interface ParticipantSearchParams {
  q: string;
  page?: number;
  limit?: number;
}

export interface ParticipantSearchResponse {
  success: boolean;
  message?: string;
  data: Participant[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    nextPage: number | null;
    prevPage: number | null;
  };
}

export interface SingleParticipantResponse {
  success: boolean;
  message?: string;
  data: {
    participant: Participant;
  };
}

export interface ParticipantsParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Types for team data
export interface TeamMember {
  _id: string;
  id: string;
  name: string;
  email: string;
  phone: string;
  role: 'leader' | 'member';
  status: 'active' | 'inactive' | 'pending';
}

export interface Team {
  _id: string;
  id: string;
  teamCode: string;
  teamName?: string;
  leader?: TeamMember;
  members: TeamMember[];
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface TeamsResponse {
  success: boolean;
  message?: string;
  data: Team[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    nextPage: number | null;
    prevPage: number | null;
  };
}

export interface TeamSearchParams {
  q: string;
  page?: number;
  limit?: number;
}

export interface TeamSearchResponse {
  success: boolean;
  message?: string;
  data: Team[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    nextPage: number | null;
    prevPage: number | null;
  };
}

export interface SingleTeamResponse {
  success: boolean;
  message?: string;
  data: {
    team: Team;
  };
}

export interface TeamsParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface UpdateTeamRequest {
  teamName?: string;
  leaderId?: string;
}

// Participant API functions
export const participantApi = {
  // Get all participants with pagination
  getParticipants: async (params?: ParticipantsParams): Promise<ParticipantsResponse> => {
    const token = localStorage.getItem('adminToken');
   
    
    const response = await axiosInstance.get('/api/v1/participants', {
      headers: {
        Authorization: `Bearer ${token}`
      },
      params: {
        page: params?.page || 1,
        limit: params?.limit || 10,
        status: params?.status,
        sortBy: params?.sortBy,
        sortOrder: params?.sortOrder || 'desc'
      }
    });
    

    return response.data;
  },

  // Get participants for specific hackathon
  getParticipantsByHackathon: async (hackathonId: string, params?: ParticipantsParams): Promise<ParticipantsResponse> => {
    const token = localStorage.getItem('adminToken');
    
    const response = await axiosInstance.get(`/api/v1/participants/hackathon/${hackathonId}`, {
      headers: {
        Authorization: `Bearer ${token}`
      },
      params: {
        page: params?.page || 1,
        limit: params?.limit || 10,
        search: params?.search,
        status: params?.status,
        sortBy: params?.sortBy || 'createdAt',
        sortOrder: params?.sortOrder || 'desc'
      }
    });
    
   
    return response.data;
  },

  // Search participants by name, email, or phone
  searchParticipants: async (searchParams: ParticipantSearchParams): Promise<ParticipantSearchResponse> => {
    const token = localStorage.getItem('adminToken');

    
    const response = await axiosInstance.get('/api/v1/participants/search', {
      headers: {
        Authorization: `Bearer ${token}`
      },
      params: {
        q: searchParams.q,
        page: searchParams.page || 1,
        limit: searchParams.limit || 10
      }
    });
    
  
    return response.data;
  },

  // Get single participant details
  getParticipantById: async (id: string): Promise<SingleParticipantResponse> => {
    const token = localStorage.getItem('adminToken');

    
    const response = await axiosInstance.get(`/api/v1/participants/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
   
    return response.data;
  },

  // Team API functions
  // Get all teams with pagination
 

  // Get teams for specific hackathon
  getTeamsByHackathon: async (hackathonId: string, params?: TeamsParams): Promise<TeamsResponse> => {
    const token = localStorage.getItem('adminToken');
    
    const response = await axiosInstance.get(`/api/v1/teams/hackathon/${hackathonId}`, {
      headers: {
        Authorization: `Bearer ${token}`
      },
      params: {
        page: params?.page || 1,
        limit: params?.limit || 10,
        search: params?.search,
        status: params?.status,
        sortBy: params?.sortBy || 'createdAt',
        sortOrder: params?.sortOrder || 'desc'
      }
    });
    
    return response.data;
  },

  // Search teams by team name or team code
  searchTeams: async (searchParams: TeamSearchParams): Promise<TeamSearchResponse> => {
    const token = localStorage.getItem('adminToken');
  
    
    const response = await axiosInstance.get('/api/v1/teams/search', {
      headers: {
        Authorization: `Bearer ${token}`
      },
      params: {
        q: searchParams.q,
        page: searchParams.page || 1,
        limit: searchParams.limit || 10
      }
    });
    

    return response.data;
  },

  // Get single team details with populated leader and members
  getTeamById: async (id: string): Promise<SingleTeamResponse> => {
    const token = localStorage.getItem('adminToken');
 
    
    const response = await axiosInstance.get(`/api/v1/teams/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
  
    return response.data;
  },

  // Update team details
  updateTeam: async (id: string, updateData: UpdateTeamRequest): Promise<SingleTeamResponse> => {
    const token = localStorage.getItem('adminToken');
  
    
    const response = await axiosInstance.put(`/api/v1/teams/${id}`, updateData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
  
    return response.data;
  },

  // Delete a team
  deleteTeam: async (id: string): Promise<{ success: boolean; message?: string }> => {
    const token = localStorage.getItem('adminToken');
 
    
    const response = await axiosInstance.delete(`/api/v1/teams/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    return response.data;
  },
};
